import { useState, useEffect, useCallback } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { useHistoryStore } from '../../stores/historyStore';
import { t } from '../../i18n';
import styles from './PropertiesPanel.module.css';

// Import fabric for side effects
import 'fabric';

export const ObjectPropertiesPanel = () => {
  const { canvas } = useCanvas();
  const { activeObject, laserOnly } = useDesignerStore();
  const { saveSnapshot } = useHistoryStore();

  const [fillColor, setFillColor] = useState('#8BC34A');
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [opacity, setOpacity] = useState(100);

  // Check if active object is a shape (not text, not image)
  const isShapeObject = activeObject &&
    activeObject.type !== 'textbox' &&
    activeObject.type !== 'i-text' &&
    activeObject.type !== 'text' &&
    activeObject.type !== 'image' &&
    !activeObject.isGuide &&
    !activeObject.isMockup &&
    !activeObject.isBaseImage &&
    !activeObject.isMaskOverlay;

  // Update local state when selection changes
  useEffect(() => {
    if (!isShapeObject || !activeObject) return;

    setFillColor((activeObject.fill as string) || '#8BC34A');
    setStrokeColor((activeObject.stroke as string) || '#000000');
    setStrokeWidth(activeObject.strokeWidth || 0);
    setOpacity(Math.round((activeObject.opacity || 1) * 100));
  }, [activeObject, isShapeObject]);

  const saveToHistory = useCallback(() => {
    if (!canvas) return;
    const state = JSON.stringify(canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay']));
    saveSnapshot(state);
  }, [canvas, saveSnapshot]);

  const updateProperty = useCallback((property: string, value: unknown) => {
    if (!canvas || !activeObject || !isShapeObject) return;

    (activeObject as unknown as Record<string, unknown>)[property] = value;
    activeObject.setCoords();
    canvas.renderAll();
    saveToHistory();
  }, [canvas, activeObject, isShapeObject, saveToHistory]);

  const handleFillColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFillColor(value);
    updateProperty('fill', value);
  };

  const handleStrokeColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStrokeColor(value);
    updateProperty('stroke', value);
  };

  const handleStrokeWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setStrokeWidth(value);
    updateProperty('strokeWidth', value);
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setOpacity(value);
    updateProperty('opacity', value / 100);
  };

  if (!isShapeObject) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>{t('design.shape.title', 'Shape Properties')}</div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.shape.fill_color', 'Fill Color')}</label>
        <div className={styles.colorInputWrapper}>
          <input
            type="color"
            className={styles.colorInput}
            value={fillColor}
            onChange={handleFillColorChange}
            disabled={laserOnly}
          />
          <span className={styles.colorValue}>{fillColor}</span>
        </div>
        {laserOnly && (
          <span className={styles.hint}>{t('design.laser_white_only', 'Laser mode: White only')}</span>
        )}
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.shape.stroke_color', 'Stroke Color')}</label>
        <div className={styles.colorInputWrapper}>
          <input
            type="color"
            className={styles.colorInput}
            value={strokeColor}
            onChange={handleStrokeColorChange}
            disabled={laserOnly}
          />
          <span className={styles.colorValue}>{strokeColor}</span>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.shape.stroke_width', 'Stroke Width')}</label>
        <div className={styles.sliderWrapper}>
          <input
            type="range"
            className={styles.slider}
            min="0"
            max="20"
            value={strokeWidth}
            onChange={handleStrokeWidthChange}
          />
          <span className={styles.sliderValue}>{strokeWidth}px</span>
        </div>
      </div>

      <div className={styles.propertyGroup}>
        <label className={styles.label}>{t('design.shape.opacity', 'Opacity')}</label>
        <div className={styles.sliderWrapper}>
          <input
            type="range"
            className={styles.slider}
            min="0"
            max="100"
            value={opacity}
            onChange={handleOpacityChange}
          />
          <span className={styles.sliderValue}>{opacity}%</span>
        </div>
      </div>
    </div>
  );
};

export default ObjectPropertiesPanel;
